/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core";
import { RestService } from "./rest-service";
import { Zaa_ZonesResponseDto } from "app/models/zoneassignedassets/Zaa_ZonesResponeDto";
import { PagedResults } from "app/models/paged-results";
import { Url_Base } from "app/rest-constants";
import { Zaa_AssetDto } from "app/models/zoneassignedassets/Zaa_AssetDto";

/**
 * Service for Zone Assigned Assets.
 */
@Injectable()
export class ZoneAssignedAssetsService {
 
    /** Constructor */
    constructor(
        private restService: RestService
    ) { }
   
    /**
     * Returns the base URL for the APIs related to the Zone Assigned Assets
     */
    private getUrl(): string {
        return `${Url_Base}/zone-assigned-assets/`;
    }

    /**
     * Returns a page of the Zaa_ZonesResponseDtos.
     */
    getZonesWithAssetCount(start: number, size: number, filterColumn?: string | null, filterValue?: string | null): Promise<PagedResults<Zaa_ZonesResponseDto>> {
        let callUrl = `${this.getUrl()}zones?start=${start}&size=${size}`;
        if (filterColumn != null && filterValue != null && filterColumn.length > 0 && filterValue.length > 0) {
            callUrl = callUrl + `&filterColumn=${filterColumn}&filterString=${filterValue}`;
        }
        return this.restService.get<PagedResults<Zaa_ZonesResponseDto>>(callUrl);
    }

    /**
     * Returns the Assets that have been assigned to the Zone with the given ID.
     */
    getAssetsForZoneId(zoneId: number): Promise<Zaa_AssetDto[]> {
        const callUrl = `${this.getUrl()}zones/${zoneId}/assigned-assets`;
        return this.restService.get<Zaa_AssetDto[]>(callUrl);
    }

    /**
     * Saves the given Assets as the ones assigned to the Zone with the given ID.
     */
    setAssetsForZoneId(zoneId: number, assets: Zaa_AssetDto[]): Promise<void> {
        const callUrl = `${this.getUrl()}zones/${zoneId}/assigned-assets`;
        return this.restService.post<void>(callUrl, assets);
    }

    /**
     * Searches for unassigned Assets and returns the list.
     * The "searchBy" value will be used to search for Assets whose Name contains that value. 
     */
    searchForUnassignedAssets(start: number, size: number, searchBy: string | null): Promise<PagedResults<Zaa_AssetDto>> {
        let callUrl = `${this.getUrl()}search-unassigned-assets?start=${start}&size=${size}`;
        if (searchBy != null && searchBy?.length > 0) {
          callUrl = `${callUrl}&filterString=${searchBy}`;
        }
        return this.restService.get<PagedResults<Zaa_AssetDto>>(callUrl);
    }

}